import React, { useEffect, useRef, useState } from 'react';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import Hls from 'hls.js';

import Image from '../Image/Image';

import styles from './Hero.module.scss';

type CarouselPhase = 'static' | 'video' | 'still_1' | 'still_2' | 'still_3';

type Props = {
  item?: PlaylistItem;
  image?: string;
  isPaused?: boolean;
};

const STILL_DURATION = 5000;

// Los stills en JWP se acceden directamente por mediaid
const getStillUrl = (mediaid: string, label: string) =>
  `https://cdn.jwplayer.com/v2/media/${mediaid}/images/${label}.jpg`;

const HeroCarousel = ({ item, image, isPaused = false }: Props) => {
  const [phase, setPhase] = useState<CarouselPhase>('static');
  const [still1Exists, setStill1Exists] = useState(false);
  const [still2Exists, setStill2Exists] = useState(false);
  const [still3Exists, setStill3Exists] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  const mediaid = item?.mediaid;
  const trailerId = (item as any)?.trailerId as string | undefined;
  const trailerUrl = trailerId ? `https://cdn.jwplayer.com/manifests/${trailerId}.m3u8` : undefined;

  const still1Url = mediaid ? getStillUrl(mediaid, 'still_1') : undefined;
  const still2Url = mediaid ? getStillUrl(mediaid, 'still_2') : undefined;
  const still3Url = mediaid ? getStillUrl(mediaid, 'still_3') : undefined;

  // Verificar qué stills existen haciendo HEAD requests
  useEffect(() => {
    if (!mediaid) return;

    setStill1Exists(false);
    setStill2Exists(false);
    setStill3Exists(false);

    const check = (url: string, setter: (v: boolean) => void) => {
      fetch(url, { method: 'HEAD' })
        .then((res) => setter(res.ok))
        .catch(() => setter(false));
    };

    check(still1Url!, setStill1Exists);
    check(still2Url!, setStill2Exists);
    check(still3Url!, setStill3Exists);
  }, [mediaid]);

  const hasVideo = !!trailerUrl;

  const getNextPhase = (current: CarouselPhase): CarouselPhase => {
    if (current === 'static')  return hasVideo ? 'video' : still1Exists ? 'still_1' : still2Exists ? 'still_2' : still3Exists ? 'still_3' : 'static';
    if (current === 'video')   return still1Exists ? 'still_1' : still2Exists ? 'still_2' : still3Exists ? 'still_3' : 'static';
    if (current === 'still_1') return still2Exists ? 'still_2' : still3Exists ? 'still_3' : 'static';
    if (current === 'still_2') return still3Exists ? 'still_3' : 'static';
    return 'static';
  };

  // Reset al cambiar de item
  useEffect(() => {
    setPhase('static');
  }, [mediaid]);

  // Timer para fases estáticas
  useEffect(() => {
    if (phase === 'video') return;
    if (isPaused) return;

    const timer = setTimeout(() => {
      setPhase((current) => getNextPhase(current));
    }, STILL_DURATION);

    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, isPaused, mediaid, still1Exists, still2Exists, still3Exists]);

  // Pausar/reanudar video
  useEffect(() => {
    if (phase !== 'video' || !videoRef.current) return;
    if (isPaused) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
  }, [isPaused, phase]);

  // Iniciar HLS
  useEffect(() => {
    if (phase !== 'video' || !trailerUrl || !videoRef.current) return;

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (Hls.isSupported()) {
      const hls = new Hls();
      hlsRef.current = hls;
      hls.loadSource(trailerUrl);
      hls.attachMedia(videoRef.current);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (!isPaused) videoRef.current?.play().catch(() => {});
      });
    } else if (videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      videoRef.current.src = trailerUrl;
      if (!isPaused) videoRef.current.play().catch(() => {});
    }

    return () => {
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, trailerUrl]);

  const handleVideoEnded = () => {
    setPhase(getNextPhase('video'));
  };

  const getImageForPhase = (): string | undefined => {
    if (phase === 'still_1') return still1Url;
    if (phase === 'still_2') return still2Url;
    if (phase === 'still_3') return still3Url;
    return image;
  };

  const isVideoPhase = phase === 'video' && hasVideo;

  return (
    <>
      <Image
        className={`${styles.poster} ${isVideoPhase ? styles.posterHidden : ''}`}
        image={getImageForPhase()}
        width={1280}
        alt=""
      />
      {hasVideo && (
        <video
          ref={videoRef}
          className={`${styles.posterVideo} ${isVideoPhase ? '' : styles.posterHidden}`}
          muted
          playsInline
          onEnded={handleVideoEnded}
        />
      )}
    </>
  );
};

export default HeroCarousel;